import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Hook to determine board permissions based on visibility and user membership
 * @param {Object} board - The board object
 * @returns {Object} - Permission flags
 */
const useBoardPermissions = (board) => {
    const user = useSelector((state) => state.user.user);

    const permissions = useMemo(() => {
        if (!board || !user) {
            return {
                canView: false,
                canEdit: false,
                canDelete: false,
                canManageMembers: false,
                canChangeSettings: false,
                isReadOnly: true,
                isAdmin: false,
                isMember: false,
                isWorkspaceMember: false,
            };
        }

        const userId = user?.user?._id;
        const boardVisibility = board.visibility || 'Private';

        // Ensure board.members is an array
        const boardMembers = Array.isArray(board.members) ? board.members : [];

        // Check if user is a board member
        // Handle both nested structure { user: { _id } } and flattened structure { _id }
        const boardMember = boardMembers.find(m => {
            const memberUserId = m.user?._id || m.user || m._id;
            return memberUserId === userId || memberUserId?.toString() === userId?.toString();
        });
        const isBoardMember = !!boardMember;
        const isBoardAdmin = boardMember?.role === 'admin';

        // Check if user is a workspace member/admin
        const isWorkspaceMember = board.workspace?.members?.some(m =>
            m.user?._id === userId || m.user === userId
        );
        const workspaceMember = board.workspace?.members?.find(m =>
            m.user?._id === userId || m.user === userId
        );
        const isWorkspaceAdmin = workspaceMember?.role === 'admin' || boardMember?.workspaceRole === 'admin';

        // Determine if user is admin (board admin or workspace admin)
        const isAdmin = isBoardAdmin || isWorkspaceAdmin;

        // Determine if user is a member (board or workspace)
        const isMember = isBoardMember || isWorkspaceMember;

        // Determine permissions based on membership and visibility
        let canView = false;
        let canEdit = false;
        let isReadOnly = true;

        // Priority 1: Board members and workspace admins ALWAYS have full access
        if (isBoardMember || isWorkspaceAdmin) {
            canView = true;
            canEdit = true;
            isReadOnly = false;
        }
        // Priority 2: Check visibility-based access for non-board members
        else if (boardVisibility === 'Public') {
            // Public boards: Workspace members can view and edit, non-workspace members can only view
            canView = true;
            canEdit = isWorkspaceMember; // Workspace members can edit
            isReadOnly = !isWorkspaceMember; // Only non-workspace members are read-only
        } else if (boardVisibility === 'Workspace') {
            // Workspace boards: All workspace members can view and edit
            canView = isWorkspaceMember;
            canEdit = isWorkspaceMember;
            isReadOnly = !isWorkspaceMember;
        } else {
            // Private boards: Only board/workspace members can view and edit
            canView = false;
            canEdit = false;
            isReadOnly = true;
        }

        return {
            canView,
            canEdit,
            canDelete: isAdmin, // Only admins can delete
            canManageMembers: isAdmin, // Only admins can manage members
            canChangeSettings: isAdmin, // Only admins can change settings
            isReadOnly,
            isAdmin,
            isMember: isBoardMember,
            isWorkspaceMember,
        };
    }, [board, user]);

    return permissions;
};

export default useBoardPermissions;

